/**
 * Общие блоки профиля человека (изменение v1.5).
 *
 * Свой профиль (U-01), предпросмотр «как меня видят» (U-02) и профиль другого
 * человека (P-04) собираются из ОДНИХ И ТЕХ ЖЕ блоков — как в Telegram, где
 * «Мой профиль» и профиль контакта отличаются только набором действий:
 *
 *   ProfileHero      круглый аватар по центру, имя с возрастом, строка статуса;
 *                    нажатие разворачивает фотографии на всю ширину карточки
 *   ProfileActions   ряд плиток-действий (иконка сверху, подпись снизу)
 *   ProfileInfo      карточка сведений «подпись сверху, значение снизу»
 *   ProfileSection   секция с заголовком: интересы, форматы, ритм, подбор
 *
 * Раньше два экрана рисовали профиль по-своему (чужой — большой карточкой
 * 4:5, свой — аватаром и плитками) и расходились при каждой правке.
 */
import { useState, type ReactNode } from 'react'

import type { PublicPhoto } from '@shared/api/types'
import { Avatar, Icon, cx, type IconName } from '@shared/ui'

import styles from './profile-view.module.css'

/** Тон строки статуса: нейтральный, «всё хорошо», «есть что исправить». */
export type ProfileStatusTone = 'default' | 'success' | 'warning'

const STATUS_TONE: Record<ProfileStatusTone, string | false> = {
  default: false,
  success: styles['statusSuccess']!,
  warning: styles['statusWarning']!,
}

function photoUrl(photo: PublicPhoto | undefined): string | null {
  if (!photo) return null
  return photo.variants.find((v) => v.name === 'medium')?.url ?? photo.variants[0]?.url ?? null
}

export function ProfileHero({
  name,
  age = null,
  photos,
  status,
  statusTone = 'default',
}: {
  name: string | null
  age?: number | null
  photos: readonly PublicPhoto[]
  /** Строка под именем: «в сети», «Анкету показывают», «был недавно». */
  status?: ReactNode
  statusTone?: ProfileStatusTone
}): React.ReactElement {
  const [expanded, setExpanded] = useState(false)
  const [index, setIndex] = useState(0)

  const safeIndex = Math.min(index, Math.max(0, photos.length - 1))
  const photo = photos[safeIndex]
  const url = photoUrl(photo)

  const title = (
    <>
      {name ?? 'Без имени'}
      {age !== null && age !== undefined ? <span className={styles['age']}>, {age}</span> : null}
    </>
  )
  const statusLine = status ? <p className={cx(styles['status'], STATUS_TONE[statusTone])}>{status}</p> : null

  /* --- Свёрнутая шапка: круглый аватар, имя, статус (профиль Telegram) --- */
  if (!expanded || !url) {
    const avatar = <Avatar name={name} photo={photo ?? null} size="2xl" />

    return (
      <header className={styles['hero']}>
        {photos.length ? (
          <button
            type="button"
            className={styles['avatarButton']}
            aria-label={photos.length > 1 ? `Фотографии: ${photos.length}` : 'Показать фото'}
            onClick={() => setExpanded(true)}
          >
            {avatar}
            {photos.length > 1 ? (
              <span className={styles['photoCount']} aria-hidden>
                <Icon name="image" size={12} />
                {photos.length}
              </span>
            ) : null}
          </button>
        ) : (
          avatar
        )}

        <h2 className={styles['name']}>{title}</h2>
        {statusLine}
      </header>
    )
  }

  /* --- Развёрнутая шапка: фото на всю ширину, имя поверх снимка --------- */
  return (
    <header className={styles['heroPhoto']}>
      <div className={styles['frame']}>
        <img className={styles['photo']} src={url} alt="" />
        <span className={styles['scrim']} aria-hidden />

        {photos.length > 1 ? (
          <>
            <div className={styles['dots']} aria-hidden>
              {photos.map((item, i) => (
                <span key={item.id} className={i === safeIndex ? styles['dotActive'] : styles['dot']} />
              ))}
            </div>
            <button
              type="button"
              className={styles['zonePrev']}
              aria-label="Предыдущее фото"
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
            />
            <button
              type="button"
              className={styles['zoneNext']}
              aria-label="Следующее фото"
              onClick={() => setIndex((i) => Math.min(photos.length - 1, i + 1))}
            />
          </>
        ) : null}

        <button
          type="button"
          className={styles['collapse']}
          aria-label="Свернуть фото"
          onClick={() => setExpanded(false)}
        >
          <Icon name="close" size={18} />
        </button>

        <div className={styles['onPhoto']}>
          <h2 className={cx(styles['name'], styles['nameOnPhoto'])}>{title}</h2>
          {status ? <p className={cx(styles['status'], styles['statusOnPhoto'])}>{status}</p> : null}
        </div>
      </div>
    </header>
  )
}

/** Ряд плиток-действий под шапкой (call/video/mute/search в Telegram). */
export function ProfileActions({ children }: { children: ReactNode }): React.ReactElement {
  return <div className={styles['tiles']}>{children}</div>
}

export function ProfileAction({
  icon,
  label,
  disabled = false,
  onClick,
}: {
  icon: IconName
  label: string
  disabled?: boolean
  onClick: () => void
}): React.ReactElement {
  return (
    <button type="button" className={styles['tile']} disabled={disabled} onClick={onClick}>
      <Icon name={icon} size={22} />
      <span className={styles['tileLabel']}>{label}</span>
    </button>
  )
}

/** Карточка сведений: никнейм, «о себе», город, род занятий, языки. */
export function ProfileInfo({ children }: { children: ReactNode }): React.ReactElement {
  return <div className={styles['infoCard']}>{children}</div>
}

export function ProfileInfoRow({
  label,
  value,
  accent = false,
  multiline = false,
  note,
  trailingIcon,
  onClick,
}: {
  label: string
  value: ReactNode
  /** Значение-ссылка (никнейм) — акцентным цветом. */
  accent?: boolean
  multiline?: boolean
  /** Пометка под значением: «на проверке». */
  note?: string
  trailingIcon?: IconName
  onClick?: () => void
}): React.ReactElement {
  const content = (
    <>
      <span className={styles['infoText']}>
        <span className={styles['infoLabel']}>{label}</span>
        <span
          className={cx(styles['infoValue'], accent && styles['infoAccent'], multiline && styles['infoWrap'])}
        >
          {value}
        </span>
        {note ? <span className={styles['infoNote']}>{note}</span> : null}
      </span>
      {trailingIcon ? (
        <span className={styles['infoTrailing']} aria-hidden>
          <Icon name={trailingIcon} size={18} />
        </span>
      ) : null}
    </>
  )

  return onClick ? (
    <button type="button" className={styles['infoRow']} onClick={onClick}>
      {content}
    </button>
  ) : (
    <div className={styles['infoRow']}>{content}</div>
  )
}

export function ProfileSection({
  title,
  action,
  children,
}: {
  title: string
  action?: ReactNode
  children: ReactNode
}): React.ReactElement {
  return (
    <section className={styles['section']}>
      <div className={styles['sectionHead']}>
        <h3 className={styles['sectionTitle']}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  )
}

export function ProfileChips({ children }: { children: ReactNode }): React.ReactElement {
  return <div className={styles['chips']}>{children}</div>
}
